import { setUserIconColor, showModal, StatBubble } from 'components/AppShell';
import { Icon } from 'components/Icon';
import { signOut } from 'components/Login';
import { type JSXChild, render } from 'engine/index';
import { Clover, Trophy } from 'lucide-static';
import { medalForEmotions, type ZintuigenEmotion, ZintuigenEmotions, type ZintuigenUpdate } from 'shared/modelTypes/facilitaire-zintuigen';
import { lateinit, onUnmount, unreachable } from 'shared/util';
import { Game } from './game';
import { rpc, State } from 'engine/rpc';
import { Timer } from 'components/Timer';

export class FacilitaireZintuigen extends Game<'facilitaire-zintuigen-spel'> {
	override maxPlayers: number = 6;

	scoreEl: HTMLElement = lateinit;

	override getGameName(): string {
		return 'Gastgerichte zintuigen spel';
	}

	override defineBottomUI(): JSXChild {
		return (
			<>
				<StatBubble color="green" label="Punten" value="0" id="score" />
			</>
		);
	}
	override onShellCreated(view: HTMLElement): void {
		super.onShellCreated(view);
		this.scoreEl = view.querySelector('#score')!;
	}

	override handleUpdate(update: ZintuigenUpdate): void {
		const sessionId = State.gameId.toString();
		const playerId = State.playerId;
		const kind = update.kind;
		// const playerId = State.playerId;

		// console.log('message', update);

		switch (kind) {
			case 'trigger-effect':
				this.triggerEffect(update);
				break;
			case 'show-login-screen':
				this.showLoginScreen();
				break;
			case 'show-register-screen':
				this.showRegisterScreen('team');
				break;
			case 'update-players-state':
				this.updatePlayersState(update);
				break;
			case 'update-player-state':
				State.name = update.player.name;
				State.color = update.player.color;
				State.score = update.player.score;

				this.shellNameEl.innerText = update.player.name;
				setUserIconColor(this.userIconEl, update.player.color);
				this.scoreEl.innerText = (State.score ?? 0).toString();
				break;
			case 'show-story-screen':
				{
					render(
						this.shellContentEl,
						<StoryScreen title={`Ronde ${update.round} - ${update.title}`} message={update.message} image={update.image} />,
					);
				}
				break;
			case 'show-question-screen':
				{
					const title = `Ronde ${update.round}: ${update.topic}`;

					render(
						this.shellContentEl,
						<QuestionScreen
							timerFrom={update.timerFrom}
							timerTo={update.timerTo}
							title={title}
							onAnswer={(emotion) => {
								rpc.sendZintuigenMessage.query({
									sessionId,
									update: {
										kind: 'submit-answer',
										playerId,
										emotion,
									},
								});
							}}
						/>,
					);
				}
				break;
			case 'show-enter-tip-wait-screen':
				{
					const title = `Round ${update.round}: ${update.topic}`;
					render(
						this.shellContentEl,
						<AnswerScreen
							title={title}
							yourAnswer={update.yourAnswer}
							message="Wacht op spelleider"
							correctAnswer={undefined}
							photos={[]}
						/>,
					);
				}
				break;

			case 'show-enter-tip-screen':
				{
					const title = `Ronde ${update.round}: ${update.topic}`;

					render(
						this.shellContentEl,
						<EnterTipScreen
							timerFrom={update.timerFrom}
							timerTo={update.timerTo}
							title={title}
							onAnswer={(tip) => {
								rpc.sendZintuigenMessage.query({
									sessionId,
									update: {
										kind: 'submit-tip',
										playerId,
										tip,
									},
								});
							}}
						/>,
					);
				}
				break;
			case 'show-answer-wait-screen':
				{
					const title = `Round ${update.round}: ${update.topic}`;
					render(
						this.shellContentEl,
						<AnswerScreen
							title={title}
							yourAnswer={update.yourAnswer}
							message="Wacht op spelleider"
							correctAnswer={undefined}
							photos={[]}
						/>,
					);
				}
				break;
			case 'show-answer-screen':
				{
					const title = `Round ${update.round}: ${update.topic}`;
					const wasPartiallyCorrect =
						update.yourAnswer && medalForEmotions(update.yourAnswer) === medalForEmotions(update.correctAnswer);
					const isCorrect = update.yourAnswer === update.correctAnswer;

					const message = (() => {
						if (isCorrect) {
							return `Jullie zijn toppers!\nJe krijgt 1 punt omdat je de juiste medaille had en 1 punt omdat\nje ook de juiste emotie hebt gekozen.`;
						}
						if (wasPartiallyCorrect) {
							return `Jullie hebben het voor een deel goed!\nJe krijgt 1 punt omdat je de juiste medaille had.`;
						}
						return `Helaas geen punten gescoord.`;
					})();

					// const goldenTipMessage = update.goldenTip
					// 	? `Gefeliciteerd jullie hadden een waardevolle tip!\nJullie ontvangen 2 punten.`
					// 	: `Helaas niet de gouden tip bedacht.\nNieuwe ronde, nieuwe kansen!`;

					render(
						this.shellContentEl,
						<AnswerScreen
							title={title}
							message={message}
							// goldenTipMessage={goldenTipMessage}
							yourAnswer={update.yourAnswer}
							correctAnswer={update.correctAnswer}
							photos={update.photos}
						/>,
					);
				}
				break;
			case 'show-tip-screen':
				{
					const goldMessage =
						State.color === update.goldTipPlayerColor
							? 'Gefeliciteerd jullie hadden de Functionele tip!\nJullie ontvangen 2 punten.'
							: `${update.goldTipPlayerName ? `${update.goldTipPlayerName} hebben` : 'Niemand heeft'} de functionele tip gewonnen.`;

					const silverMessage =
						State.color === update.silverTipPlayerColor
							? 'Gefeliciteerd jullie hadden de gastgerichte tip!\nJullie ontvangen 1 punt.'
							: `${update.silverTipPlayerName ? `${update.silverTipPlayerName} hebben` : 'Niemand heeft'} de gastgerichte tip gewonnen.`;

					render(this.shellContentEl, <TipScreen goldMessage={goldMessage} silverMessage={silverMessage} />);
				}

				break;
			case 'show-results-screen':
				render(this.shellContentEl, <ResultsScreen results={update.players} />);

				break;
			default:
				unreachable(kind);
		}
	}
}

// export function Waitscreen({ message }: { message: string }) {
// 	return (
// 		<div class="flex w-full flex-col items-center justify-center gap-2 p-6 md:max-w-screen-md">
// 			<Icon icon={Hourglass} class="size-20 text-gray-300" />
// 			<div class="text-center text-xl text-gray-800">
// 				<span class="font-bold text-gray-900">{message}</span>
// 			</div>
// 		</div>
// 	);
// }

export function StoryScreen({ title, button, message, image }: { title: string; button?: JSXChild; message: string; image: string }) {
	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center justify-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="mb-2 text-3xl font-bold text-gray-900 sm:mb-6 sm:text-6xl">{title}</h2>
			<pre class="w-full whitespace-normal text-left font-sans text-gray-800">{message}</pre>
			{button}
			<img src={image} class="mt-4 max-h-[50vh] w-full object-contain" />
		</div>
	);
}

export function QuestionScreen({
	title,
	onAnswer,
	timerFrom,
	timerTo,
}: {
	timerFrom: number;
	timerTo: number;
	title: string;
	onAnswer?: (emotion: ZintuigenEmotion) => void;
}) {
	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:mb-6 lg:text-6xl">{title}</h2>
			<Timer timerFrom={timerFrom} timerTo={timerTo} />
			<EmotionPicker onPick={onAnswer} />
		</div>
	);
}

export function EnterTipScreen({
	title,
	onAnswer,
	timerFrom,
	timerTo,
}: {
	timerFrom: number;
	timerTo: number;
	title: string;
	onAnswer: (tip: string) => void;
}) {
	return (
		<div class="flex h-fit w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:mb-6 lg:text-6xl">{title}</h2>
			<Timer timerFrom={timerFrom} timerTo={timerTo} />
			<pre class="w-full whitespace-normal text-left font-sans text-gray-800">Vul hieronder jullie tip in:</pre>
			<textarea
				id="tip-input"
				class="h-32 w-full max-w-sm rounded-md border p-2"
				placeholder="Schrijf hier je tip..."
				on:input={(e: Event) => {
					const v = (e.target as HTMLInputElement).value;
					onAnswer(v.trim());
				}}
			></textarea>
		</div>
	);
}

export function TipScreen({ goldMessage, silverMessage }: { goldMessage: string; silverMessage: string }) {
	return (
		<div class="flex w-full flex-col items-center justify-center gap-2 p-6 md:max-w-screen-md">
			<Icon icon={Clover} class="size-20 text-gray-300" />
			<div class="text-center text-xl text-gray-800">
				<span class="font-bold text-gray-900">{goldMessage}</span>
				<br />
				<span class="font-bold text-gray-900">{silverMessage}</span>
			</div>
		</div>
	);
}

export function AnswerScreen({
	title,
	message,
	// goldenTipMessage,
	yourAnswer,
	photos,
	correctAnswer,
}: {
	title: string;
	message: string;
	// goldenTipMessage: string;
	photos: string[];
	yourAnswer: ZintuigenEmotion | undefined;
	correctAnswer: ZintuigenEmotion | undefined;
}) {
	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:mb-6 lg:text-6xl">{title}</h2>
			<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800">{message}</pre>
			{/* <pre class="mt-2 text-center font-sans text-gray-600">{goldenTipMessage}</pre> */}
			<EmotionPicker yourAnswer={yourAnswer} correctAnswer={correctAnswer} />
			<div class="flex items-center justify-center gap-3">
				{photos.map((p) => (
					<img
						src={p}
						on:click={() =>
							showModal(<img src={p} />, {
								passiveDismiss: true,
							})
						}
						class="size-16 rounded-md border border-gray-400 bg-gray-100 object-cover shadow-sm"
					/>
				))}
			</div>
		</div>
	);
}

export function ResultsScreen({ results }: { results: { name: string; color: string; score: number }[] }) {
	return (
		<div class="flex w-full flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:mb-6 lg:text-6xl">Einduitslag</h2>
			<table class="overflow-hidden rounded-md shadow-sm">
				<thead>
					<tr class="bg-gray-100">
						<th class="px-5 py-1">Rank</th>
						<th class="px-5 py-1">Pion</th>
						<th class="px-5 py-1">Naam</th>
						<th class="px-5 py-1">Punten</th>
					</tr>
				</thead>
				<tbody>
					{results.map((r, i) => {
						const highlight = State.color == r.color;

						return (
							<tr class={highlight ? 'bg-yellow-100 ring-2 ring-yellow-400' : 'even:bg-gray-50'}>
								<td class="px-5 py-1">
									<div class="inline-flex items-baseline gap-3">
										<span class="font-bold">{i + 1}</span>
										{i == 0 && <Icon icon={Trophy} class="size-4 text-orange-400" />}
										{i == 1 && <Icon icon={Trophy} class="size-4 text-gray-400" />}
										{i == 2 && <Icon icon={Trophy} class="size-4 text-orange-700" />}
										{i >= 3 && <Icon icon={Trophy} class="size-4 opacity-0" />}
									</div>
								</td>
								<td class="px-5 py-1">{r.color}</td>
								<td class="px-5 py-1">{r.name}</td>
								<td class="px-5 py-1">{r.score}</td>
							</tr>
						);
					})}
				</tbody>
			</table>
			<button class="rounded-md bg-gray-200 p-4 text-gray-900" on:click={signOut}>
				Spel afsluiten
			</button>
		</div>
	);
}

function EmotionPicker({
	yourAnswer,
	correctAnswer,
	onPick,
}: {
	yourAnswer?: string;
	correctAnswer?: string;
	onPick?: (value: ZintuigenEmotion) => void;
}) {
	const view: HTMLElement = (
		<div
			on:click={
				onPick
					? (e: MouseEvent) => {
							const rect = view.getBoundingClientRect();
							const centerX = rect.left + rect.width / 2;
							const centerY = rect.top + rect.height / 2;
							const dx = e.clientX - centerX;
							const dy = e.clientY - centerY;
							const maxDistance = Math.sqrt((rect.width / 2) ** 2 + (rect.height / 2) ** 2);

							const distance = Math.sqrt(dx * dx + dy * dy) / maxDistance; // Normalize distance (0-1)
							let angle = Math.atan2(dy, dx);
							angle = (angle + Math.PI / 2 + 2 * Math.PI) % (2 * Math.PI); // Adjust angle to 0 at top, clockwise
							const normalizedAngle = angle / (2 * Math.PI); // Normalize angle (0-1)

							if (distance < 0.62) {
								const index = Math.floor(normalizedAngle * ZintuigenEmotions.length);
								const selectedEmotion = ZintuigenEmotions[index];
								// console.log(selectedEmotion);
								onPick(selectedEmotion);

								placeMarker(selectedEmotion, marker);
							} else {
								// marker.style.display = 'none';
							}
						}
					: undefined
			}
			class="relative size-full"
		>
			<img class="pointer-events-none size-full" src={'/facilitaire-zintuigen/medal.png'} />
			<div
				your-marker
				style="display:none"
				class="absolute size-6 -translate-x-1/2 -translate-y-1/2 rounded-full border border-theme-700 bg-theme-600 shadow-md md:size-10"
			></div>
			<div
				correct-marker
				style="display:none"
				class="absolute size-6 -translate-x-1/2 -translate-y-1/2 rounded-full border border-green-700 bg-green-600 shadow-md md:size-10"
			></div>
		</div>
	);

	const marker: HTMLElement = view.querySelector('[your-marker]') as HTMLElement;
	const correctMarker: HTMLElement = view.querySelector('[correct-marker]') as HTMLElement;

	const placeMarker = (emotion: string | undefined, markerEl: HTMLElement) => {
		const rect = view.getBoundingClientRect();
		const maxDistance = Math.sqrt((rect.width / 2) ** 2 + (rect.height / 2) ** 2);
		const index = ZintuigenEmotions.indexOf(emotion as ZintuigenEmotion);
		if (emotion && index !== -1) {
			const sliceAngle = ((index + 0.5) / ZintuigenEmotions.length - 0.24) * 2 * Math.PI;
			const markerX = rect.width / 2 + maxDistance * 0.31 * Math.cos(sliceAngle);
			const markerY = rect.height / 2 + maxDistance * 0.31 * Math.sin(sliceAngle);

			markerEl.style.left = `${markerX}px`;
			markerEl.style.top = `${markerY}px`;
			markerEl.style.display = '';
		} else {
			markerEl.style.display = 'none';
		}
	};

	if (!onPick) {
		const r = new ResizeObserver(() => {
			placeMarker(yourAnswer, marker);
			placeMarker(correctAnswer, correctMarker);
		});
		r.observe(view);
		onUnmount(view, () => r.disconnect());
	}

	return view;
}
